import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../utils/cn';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { Plus, Trash2, Copy, Eye, Calendar, Clock, Settings, Save, RotateCcw, Download, Palette, ArrowLeft } from 'lucide-react';
import Icon from '../../components/AppIcon';
import { 
  saveUserAvailability, 
  getUserAvailability, 
  saveActivityTypes, 
  getActivityTypes,
  convertAvailabilityToWeeklySchedule,
  convertWeeklyScheduleToAvailability
} from '../../repos/availabilityRepo';


const AvailabilityConfiguration = () => {
  const navigate = useNavigate();
  const { session } = useAuth();
  const [activeTab, setActiveTab] = useState('schedule');
  const [weeklySchedule, setWeeklySchedule] = useState({
    monday: { enabled: false, slots: [] },
    tuesday: { enabled: false, slots: [] },
    wednesday: { enabled: false, slots: [] },
    thursday: { enabled: false, slots: [] },
    friday: { enabled: false, slots: [] },
    saturday: { enabled: false, slots: [] },
    sunday: { enabled: false, slots: [] }
  });
  const [activityTypes, setActivityTypes] = useState([
    { id: 1, name: 'Atendimento Individual', duration: 60, color: '#4B1E1E', description: 'Sessão individual de mentoria' },
    { id: 2, name: 'Consultoria em Grupo', duration: 90, color: '#C89D4B', description: 'Sessão em grupo até 5 pessoas' },
    { id: 3, name: 'Workshop', duration: 120, color: '#2E4E4E', description: 'Workshop temático' }
  ]);
  const [permissions, setPermissions] = useState('publico');
  const [isSaving, setIsSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [selectedDay, setSelectedDay] = useState('monday');
  const [isLoading, setIsLoading] = useState(true);

  const daysOfWeek = [
    { key: 'monday', label: 'Segunda-feira', shortLabel: 'SEG' },
    { key: 'tuesday', label: 'Terça-feira', shortLabel: 'TER' },
    { key: 'wednesday', label: 'Quarta-feira', shortLabel: 'QUA' },
    { key: 'thursday', label: 'Quinta-feira', shortLabel: 'QUI' },
    { key: 'friday', label: 'Sexta-feira', shortLabel: 'SEX' },
    { key: 'saturday', label: 'Sábado', shortLabel: 'SAB' },
    { key: 'sunday', label: 'Domingo', shortLabel: 'DOM' }
  ];

  const timeSlots = Array.from({ length: 24 * 4 }, (_, i) => {
    const hours = Math.floor(i / 4);
    const minutes = (i % 4) * 15;
    const timeString = `${hours?.toString()?.padStart(2, '0')}:${minutes?.toString()?.padStart(2, '0')}`;
    return { value: timeString, label: timeString };
  });

  const permissionOptions = [
    { value: 'publico', label: 'Público', description: 'Visível para todos os usuários' },
    { value: 'privado', label: 'Privado', description: 'Apenas você pode ver os detalhes' },
    { value: 'restrito', label: 'Restrito', description: 'Visível apenas para usuários selecionados' }
  ];

  // Toggle day availability
  const toggleDayAvailability = (day) => {
    setWeeklySchedule(prev => ({
      ...prev,
      [day]: {
        ...prev?.[day],
        enabled: !prev?.[day]?.enabled,
        slots: !prev?.[day]?.enabled ? prev?.[day]?.slots : []
      }
    }));
  };

  // Add time slot to a day
  const addTimeSlot = (day) => {
    const newSlot = {
      id: Date.now(),
      startTime: '09:00',
      endTime: '10:00',
      activityType: activityTypes?.[0]?.id || 1
    };
    
    setWeeklySchedule(prev => ({
      ...prev,
      [day]: {
        ...prev?.[day],
        slots: [...prev?.[day]?.slots, newSlot]
      }
    }));
  };

  // Remove time slot
  const removeTimeSlot = (day, slotId) => {
    setWeeklySchedule(prev => ({
      ...prev,
      [day]: {
        ...prev?.[day],
        slots: prev?.[day]?.slots?.filter(slot => slot?.id !== slotId)
      }
    }));
  };

  // Update time slot
  const updateTimeSlot = (day, slotId, field, value) => {
    setWeeklySchedule(prev => ({
      ...prev,
      [day]: {
        ...prev?.[day],
        slots: prev?.[day]?.slots?.map(slot => 
          slot?.id === slotId ? { ...slot, [field]: value } : slot
        )
      }
    }));
  };

  // Validate time range for a slot
  const validateTimeRange = (startTime, endTime) => {
    if (!startTime || !endTime) return { isValid: true, message: '' };
    
    const start = new Date(`2000-01-01 ${startTime}`);
    const end = new Date(`2000-01-01 ${endTime}`);
    
    if (end <= start) {
      return {
        isValid: false,
        message: 'Horário de término deve ser posterior ao horário de início'
      };
    }
    
    return { isValid: true, message: '' };
  };

  // Get time validation error for a slot
  const getTimeError = (day, slotId) => {
    const slot = weeklySchedule[day]?.slots?.find(s => s.id === slotId);
    if (!slot) return '';
    
    const validation = validateTimeRange(slot.startTime, slot.endTime);
    return validation.isValid ? '' : validation.message;
  };

  // Copy schedule from one day to another
  const copySchedule = (fromDay, toDay) => {
    const sourceSchedule = weeklySchedule?.[fromDay];
    const copiedSlots = sourceSchedule?.slots?.map(slot => ({
      ...slot,
      id: Date.now() + Math.random()
    }));

    setWeeklySchedule(prev => ({
      ...prev,
      [toDay]: {
        enabled: sourceSchedule?.enabled,
        slots: copiedSlots
      }
    }));
  };

  // Add new activity type
  const addActivityType = () => {
    const newType = {
      id: Date.now(),
      name: 'Novo Tipo de Atividade',
      duration: 60,
      color: '#4B1E1E',
      description: ''
    };
    setActivityTypes(prev => [...prev, newType]);
  };

  // Update activity type
  const updateActivityType = (id, field, value) => {
    setActivityTypes(prev =>
      prev?.map(type => 
        type?.id === id ? { ...type, [field]: value } : type
      )
    );
  };

  // Remove activity type
  const removeActivityType = (id) => {
    setActivityTypes(prev => prev?.filter(type => type?.id !== id));
  };

  // Load data from database on component mount
  useEffect(() => {
    const loadData = async () => {
      if (!session?.systemUser?.id) return;
      
      try {
        setIsLoading(true);
        
        // Load availability
        const availability = await getUserAvailability(session.systemUser.id);
        const schedule = convertAvailabilityToWeeklySchedule(availability);
        setWeeklySchedule(schedule);
        
        // Load activity types
        const types = await getActivityTypes(session.systemUser.id);
        if (types.length > 0) {
          setActivityTypes(types.map(type => ({
            id: type.id,
            name: type.name,
            duration: type.duration,
            color: type.color,
            description: type.description || ''
          })));
        }
      } catch (error) {
        console.error('❌ Erro ao carregar dados:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [session?.systemUser?.id]);

  // Save configuration
  const handleSave = async () => {
    if (!session?.systemUser?.id) {
      alert('Usuário não autenticado');
      return;
    }
    
    setIsSaving(true);
    try {
      // Save availability
      const availabilityData = convertWeeklyScheduleToAvailability(weeklySchedule, session.systemUser.id);
      await saveUserAvailability(availabilityData);
      
      // Save activity types
      const activityTypesData = activityTypes.map(type => ({
        id: type.id?.toString(),
        user_id: session.systemUser.id,
        name: type.name,
        duration: type.duration,
        color: type.color,
        description: type.description
      }));
      await saveActivityTypes(activityTypesData);
      
      console.log('✅ Configurações salvas com sucesso');
      alert('Configurações salvas com sucesso!');
    } catch (error) {
      console.error('❌ Erro ao salvar:', error);
      alert('Erro ao salvar configurações. Tente novamente.');
    } finally {
      setIsSaving(false);
    }
  };

  // Export configuration
  const handleExport = () => {
    const config = {
      weeklySchedule,
      activityTypes,
      permissions,
      exportedAt: new Date()?.toISOString()
    };
    
    const dataStr = JSON.stringify(config, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    
    const exportFileDefaultName = 'disponibilidade-calendar.json';
    
    const linkElement = document.createElement('a');
    linkElement?.setAttribute('href', dataUri);
    linkElement?.setAttribute('download', exportFileDefaultName);
    linkElement?.click();
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'schedule':
        return (
          <div className="availability-form space-y-6">
            {/* Mobile Day Selector */}
            <div className="md:hidden">
              <Select
                value={selectedDay}
                onChange={setSelectedDay}
                options={daysOfWeek?.map(day => ({
                  value: day?.key,
                  label: day?.label
                }))}
                label="Selecionar Dia"
              />
            </div>
            {/* Desktop Grid / Mobile Single Day */}
            <div className="space-y-6">
              <div className="hidden md:grid md:grid-cols-7 gap-4">
                {daysOfWeek?.map((day) => (
                  <DayScheduleCard
                    key={day?.key}
                    day={day}
                    schedule={weeklySchedule?.[day?.key]}
                    activityTypes={activityTypes}
                    timeSlots={timeSlots}
                    onToggleDay={() => toggleDayAvailability(day?.key)}
                    onAddSlot={() => addTimeSlot(day?.key)}
                    onRemoveSlot={(slotId) => removeTimeSlot(day?.key, slotId)}
                    onUpdateSlot={(slotId, field, value) => updateTimeSlot(day?.key, slotId, field, value)}
                    onCopyFrom={(fromDay) => copySchedule(fromDay, day?.key)}
                    allDays={daysOfWeek}
                    getTimeError={getTimeError}
                  />
                ))}
              </div>

              {/* Mobile Single Day View */}
              <div className="md:hidden">
                <DayScheduleCard
                  day={daysOfWeek?.find(d => d?.key === selectedDay)}
                  schedule={weeklySchedule?.[selectedDay]}
                  activityTypes={activityTypes}
                  timeSlots={timeSlots}
                  onToggleDay={() => toggleDayAvailability(selectedDay)}
                  onAddSlot={() => addTimeSlot(selectedDay)}
                  onRemoveSlot={(slotId) => removeTimeSlot(selectedDay, slotId)}
                  onUpdateSlot={(slotId, field, value) => updateTimeSlot(selectedDay, slotId, field, value)}
                  onCopyFrom={(fromDay) => copySchedule(fromDay, selectedDay)}
                  allDays={daysOfWeek}
                  getTimeError={getTimeError}
                />
              </div>
            </div>
            {/* Bulk Actions */}
            <div className="flex flex-wrap gap-4 p-4 bg-muted rounded-lg">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const enabledDays = Object.keys(weeklySchedule)?.filter(day => weeklySchedule?.[day]?.enabled);
                  if (enabledDays?.length > 0) {
                    const template = weeklySchedule?.[enabledDays?.[0]];
                    daysOfWeek?.forEach(day => {
                      if (day?.key !== enabledDays?.[0]) {
                        copySchedule(enabledDays?.[0], day?.key);
                      }
                    });
                  }
                }}
              >
                <Copy className="w-4 h-4 mr-2" />
                Aplicar a Todos os Dias
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setWeeklySchedule({
                    monday: { enabled: false, slots: [] },
                    tuesday: { enabled: false, slots: [] },
                    wednesday: { enabled: false, slots: [] },
                    thursday: { enabled: false, slots: [] },
                    friday: { enabled: false, slots: [] },
                    saturday: { enabled: false, slots: [] },
                    sunday: { enabled: false, slots: [] }
                  });
                }}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Limpar Tudo
              </Button>
            </div>
          </div>
        );

      case 'activities':
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Tipos de Atividade</h3>
                <p className="text-sm text-muted-foreground">Configure os tipos de atendimento disponíveis</p>
              </div>
              <Button onClick={addActivityType}>
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Tipo
              </Button>
            </div>
            <div className="grid gap-4">
              {activityTypes?.map((type) => (
                <div key={type?.id} className="p-4 border border-border rounded-lg space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Nome da Atividade"
                      value={type?.name}
                      onChange={(e) => updateActivityType(type?.id, 'name', e?.target?.value)}
                      placeholder="Ex: Atendimento Individual"
                    />
                    
                    <div className="flex gap-2">
                      <Input
                        label="Duração (minutos)"
                        type="number"
                        value={type?.duration}
                        onChange={(e) => updateActivityType(type?.id, 'duration', parseInt(e?.target?.value))}
                        className="flex-1"
                      />
                      
                      <div className="flex flex-col">
                        <label className="text-sm font-medium mb-2">Cor</label>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-10 h-10 rounded-md border border-border cursor-pointer"
                            style={{ backgroundColor: type?.color }}
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'color';
                              input.value = type?.color;
                              input?.addEventListener('change', (e) => {
                                updateActivityType(type?.id, 'color', e?.target?.value);
                              });
                              input?.click();
                            }}
                          />
                          <Palette className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <Input
                    label="Descrição"
                    value={type?.description}
                    onChange={(e) => updateActivityType(type?.id, 'description', e?.target?.value)}
                    placeholder="Breve descrição do tipo de atendimento"
                  />

                  <div className="flex justify-end">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => removeActivityType(type?.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remover
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'permissions':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground">Configurações de Privacidade</h3>
              <p className="text-sm text-muted-foreground">Defina quem pode visualizar sua disponibilidade</p>
            </div>

            <div className="space-y-4">
              <Select
                label="Nível de Privacidade"
                value={permissions}
                onChange={setPermissions}
                options={permissionOptions}
                description="Escolha o nível de privacidade para seu calendário"
              />

              {permissions === 'restrito' && (
                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-medium mb-3">Usuários com Acesso</h4>
                  <div className="space-y-2">
                    <Input placeholder="Digite o email ou nome do usuário" />
                    <Button size="sm">Adicionar Usuário</Button>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 p-4 bg-blue-50 rounded-lg">
                <Eye className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-900">
                    Visualização Externa
                  </p>
                  <p className="text-xs text-blue-700">
                    {permissions === 'publico' && 'Outros usuários podem ver seus horários disponíveis e ocupados'}
                    {permissions === 'privado' && 'Outros usuários só veem que você está ocupado, sem detalhes'}
                    {permissions === 'restrito' && 'Apenas usuários autorizados podem ver sua disponibilidade'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const breadcrumbItems = [
    { label: 'Início', path: '/dashboard-home' },
    { label: 'Calendário', path: '/calendar-main-interface' },
    { label: 'Configurar Disponibilidade', path: '/availability-configuration', isActive: true }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto p-4">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb customItems={breadcrumbItems} />
          </div>
          
          {/* Header */}
          <div className="mb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Configuração de Disponibilidade
              </h1>
              <p className="text-muted-foreground">
                Configure seus horários disponíveis, tipos de atividade e permissões de visualização
              </p>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/calendar-main-interface')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar ao Calendário
              </Button>
              
              <Button
                variant="outline"
                onClick={() => setPreviewMode(!previewMode)}
              >
                <Eye className="w-4 h-4 mr-2" />
                {previewMode ? 'Editar' : 'Visualizar'}
              </Button>
              
              <Button
                variant="outline"
                onClick={handleExport}
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
              
              <Button
                onClick={handleSave}
                loading={isSaving}
                disabled={isSaving}
              >
                <Save className="w-4 h-4 mr-2" />
                {isSaving ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-border">
            <nav className="flex space-x-8">
              {[
                { key: 'schedule', label: 'Agenda Semanal', icon: Calendar },
                { key: 'activities', label: 'Tipos de Atividade', icon: Clock },
                { key: 'permissions', label: 'Permissões', icon: Settings }
              ]?.map((tab) => {
                const Icon = tab?.icon;
                return (
                  <button
                    key={tab?.key}
                    onClick={() => setActiveTab(tab?.key)}
                    className={cn(
                      "py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2",
                      activeTab === tab?.key
                        ? "border-primary text-primary" :"border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground"
                    )}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{tab?.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-card rounded-lg border border-border p-6">
          {renderTabContent()}
        </div>

        {/* Preview Mode Overlay */}
        {previewMode && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-card rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Visualização Externa</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Assim outros usuários verão sua disponibilidade:
              </p>
              <div className="space-y-2">
                {Object.entries(weeklySchedule)?.map(([day, schedule]) => (
                  <div key={day} className="flex justify-between text-sm">
                    <span className="capitalize">{daysOfWeek?.find(d => d?.key === day)?.label}</span>
                    <span className={schedule?.enabled ? "text-success" : "text-muted-foreground"}>
                      {schedule?.enabled ? `${schedule?.slots?.length} slots` : 'Indisponível'}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex justify-end">
                <Button onClick={() => setPreviewMode(false)}>Fechar</Button>
              </div>
            </div>
          </div>
        )}
        </div>
      </main>
    </div>
  );
};

// Day Schedule Card Component
const DayScheduleCard = ({ 
  day, 
  schedule, 
  activityTypes, 
  timeSlots, 
  onToggleDay, 
  onAddSlot, 
  onRemoveSlot, 
  onUpdateSlot, 
  onCopyFrom,
  allDays,
  getTimeError
}) => {
  const [showCopyMenu, setShowCopyMenu] = useState(false);

  return (
    <div className={cn(
      "border border-border rounded-lg p-4 transition-all",
      schedule?.enabled ? "bg-card" : "bg-muted/50"
    )}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={schedule?.enabled}
            onChange={onToggleDay}
            className="rounded border-border"
          />
          <div>
            <h4 className="font-medium text-sm">{day?.label}</h4>
            <span className="text-xs text-muted-foreground">{day?.shortLabel}</span>
          </div>
        </div>
        
        {schedule?.enabled && (
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowCopyMenu(!showCopyMenu)}
            >
              <Copy className="w-3 h-3" />
            </Button>
            
            {showCopyMenu && (
              <div className="absolute right-0 top-8 bg-card border border-border rounded-md shadow-lg p-2 z-10 min-w-32">
                <p className="text-xs text-muted-foreground mb-2">Copiar de:</p>
                {allDays?.filter(d => d?.key !== day?.key)?.map(sourceDay => (
                  <button
                    key={sourceDay?.key}
                    onClick={() => {
                      onCopyFrom(sourceDay?.key);
                      setShowCopyMenu(false);
                    }}
                    className="block w-full text-left text-xs hover:bg-muted p-1 rounded"
                  >
                    {sourceDay?.shortLabel}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {schedule?.enabled && (
        <div className="space-y-3">
          {schedule?.slots?.map((slot) => (
            <div key={slot?.id} className="p-4 bg-muted rounded-md space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Início</label>
                  <Input
                    type="time"
                    value={slot?.startTime}
                    onChange={(e) => onUpdateSlot(slot?.id, 'startTime', e.target.value)}
                    placeholder="09:00"
                    className="min-w-[120px]"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Fim</label>
                  <Input
                    type="time"
                    value={slot?.endTime}
                    onChange={(e) => onUpdateSlot(slot?.id, 'endTime', e.target.value)}
                    placeholder="10:00"
                    className="min-w-[120px]"
                  />
                </div>
              </div>
              
              {/* Time validation error */}
              {getTimeError && (
                <div className="text-sm text-destructive">
                  {getTimeError(day.key, slot.id)}
                </div>
              )}
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Tipo de Atividade</label>
                <Select
                  options={activityTypes?.map(type => ({
                    value: type?.id,
                    label: type?.name,
                    description: `${type?.duration}min`
                  }))}
                  value={slot?.activityType}
                  onChange={(value) => onUpdateSlot(slot?.id, 'activityType', value)}
                  placeholder="Tipo de Atividade"
                />
              </div>
              
              <Button
                variant="destructive"
                size="sm"
                onClick={() => onRemoveSlot(slot?.id)}
                className="w-full"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remover
              </Button>
            </div>
          ))}

          <Button
            variant="outline"
            size="sm"
            onClick={onAddSlot}
            className="w-full"
          >
            <Plus className="w-3 h-3 mr-2" />
            Adicionar Horário
          </Button>
        </div>
      )}
    </div>
  );
};

export default AvailabilityConfiguration;